import dayjs from "dayjs";
import { Form, Formik } from "formik";
import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { classicDateFormat, dateFormateYearMonth } from "../../../../helpers";
import { AntSelect, CommonButton, DatePicker } from "../../../index";

/**
 * TransactionsHistoryFilter component renders the filter form for the transactions history page.
 * It uses the Formik library for form handling and AntSelect for dropdowns.
 * @component
 * @param {Object} props - Props for the component.
 * @param {Function} props.onSubmit - Function to handle form submission.
 * @param {Function} props.onReset - Function to handle form reset.
 * @param {Object} props.filterData - Data to pre-fill in the filter form.
 */
function TransactionsHistoryFilter({ onSubmit, onReset, filterData }) {
  const { t } = useTranslation();
  const [disableReset, setDisableReset] = useState(true);
  const [disableSubmit, setDisableSubmit] = useState(false);

  const initialValues = {
    startDate: filterData?.startDate || "",
    endDate: filterData?.endDate || "",
    status: filterData?.status || undefined,
    subscriptionType: filterData?.subscriptionType || undefined,
  };
  const subscriberTypeData = [
    { id: "all", name: "All" },
    {
      id: "monthly",
      name: "Monthly",
    },
    {
      id: "yearly",
      name: "Yearly",
    },
  ];
  const arrayOfData = [
    { id: "all", name: "All" },
    {
      id: "succeeded",
      name: "Success Transactions",
    },
    {
      id: "pending",
      name: "Pending Transactions",
    },
    {
      id: "failed",
      name: "Failed Transactions",
    },
  ];
  /**
   * Handles form reset and enables/disables the reset button accordingly.
   * @param {Function} resetForm - Formik's resetForm function.
   */
  const onHandleReset = (resetForm) => {
    resetForm({});
    setDisableReset(true);
    if (
      filterData.startDate ||
      filterData.endDate ||
      filterData.status ||
      filterData.subscriptionType
    ) {
      onReset();
    }
  };

  return (
    <Formik
      initialValues={{ ...initialValues }}
      onSubmit={disableSubmit && onSubmit}
      enableReinitialize
      validate={(e) => {
        if (e.startDate || e.endDate || e.status || e.subscriptionType) {
          setDisableReset(false);
          setDisableSubmit(true);
        } else {
          setDisableReset(true);
          setDisableSubmit(false);
        }
      }}
    >
      {(props) => {
        const { values } = props;
        return (
          <Form>
            <div className="dropdown-body dropdown-body-rg">
              <Row className="g-3">
                <Col md="12">
                  <div className="form-group ">
                    <label className="overline-title overline-title-alt">
                      {t("text.common.date")}
                    </label>
                    <div className="dateRange rangeBetween d-flex flex-column flex-sm-row">
                      <div className="form-control-wrap">
                        <DatePicker
                          name="startDate"
                          id="startDate"
                          className="form-control date-picker shadow-none"
                          placeholder={classicDateFormat}
                          format={classicDateFormat}
                          requiredDateTimeFormat={dateFormateYearMonth}
                          onChange={(_, dateString) =>
                            props.setFieldValue(
                              "startDate",
                              dayjs(dateString).format("YYYY-DD-MM")
                            )
                          }
                          value={
                            props.values?.startDate !== ""
                              ? dayjs(props.values?.startDate)
                              : ""
                          }
                        />
                      </div>
                      <div className="dateBetween align-self-center mx-0 mx-sm-1 my-sm-0 my-1">
                        {t("text.common.to")}
                      </div>
                      <div className="form-control-wrap">
                        <DatePicker
                          name="endDate"
                          id="endDate"
                          className="form-control date-picker shadow-none"
                          placeholder={classicDateFormat}
                          format={classicDateFormat}
                          requiredDateTimeFormat={dateFormateYearMonth}
                          onChange={(_, dateString) =>
                            props.setFieldValue(
                              "endDate",
                              dayjs(dateString).format("YYYY-DD-MM")
                            )
                          }
                          value={
                            props.values?.endDate !== ""
                              ? dayjs(props.values?.endDate)
                              : ""
                          }
                        />
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md="12">
                  <div className="form-group ">
                    <label className="overline-title overline-title-alt">
                      {t("text.common.subscriptionType")}
                    </label>
                    <AntSelect
                      size="medium"
                      id="subscriptionType"
                      extraClassName="form-control"
                      name="subscriptionType"
                      disabled={false}
                      variant="standard"
                      placeholder="Select"
                      arrayOfData={subscriberTypeData}
                      setFieldValue={props.handleChange}
                    />
                  </div>
                </Col>

                <Col md="12">
                  <div className="form-group ">
                    <label className="overline-title overline-title-alt">
                      {t("text.common.status")}
                    </label>
                    <AntSelect
                      size="medium"
                      id="status"
                      extraClassName="form-control"
                      name="status"
                      disabled={false}
                      variant="standard"
                      placeholder="Select"
                      arrayOfData={arrayOfData}
                      setFieldValue={props.handleChange}
                      value={values.status}
                    />
                  </div>
                </Col>
              </Row>
            </div>
            <div className="dropdown-foot between">
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!disableReset) {
                    onHandleReset(props.resetForm, props);
                  }
                }}
                type="button"
                className="clickable text-info"
              >
                {t("text.common.reset")}
              </Link>

              <CommonButton
                type="submit"
                extraClassName="btn btn-primary"
                htmlType="submit"
              >
                {t("text.common.filter")}
              </CommonButton>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

export default withTranslation()(TransactionsHistoryFilter);
